import { Link } from 'react-router-dom'
import { useEffect, useState, FormEvent } from 'react'
import { scrollToFirstError } from '@/lib/form'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

interface FieldErrors {
  name?: string
  email?: string
  password?: string
  confirmPassword?: string
  terms?: string
}

export default function InscriptionEntreprisePage() {
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [success, setSuccess] = useState(false)
  // Arrivée depuis « Trouver un expert » (?next=/missions/nouvelle?…) : on y renvoie après connexion
  const [next, setNext] = useState('')
  useEffect(() => {
    const n = new URLSearchParams(window.location.search).get('next') ?? ''
    if (n.startsWith('/') && !n.startsWith('//')) setNext(n)
  }, [])
  const [isCnpas, setIsCnpas] = useState(false)

  function validate(fd: FormData): FieldErrors {
    const errs: FieldErrors = {}
    const name = fd.get('name') as string
    const email = fd.get('email') as string
    const password = fd.get('password') as string
    const confirmPassword = fd.get('confirmPassword') as string
    const terms = fd.get('terms')

    if (!name || name.trim().length < 2) errs.name = 'Nom de l\'entreprise requis (min. 2 caractères)'
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Adresse email invalide'
    if (!password || password.length < 8) errs.password = 'Mot de passe requis (min. 8 caractères)'
    if (password !== confirmPassword) errs.confirmPassword = 'Les mots de passe ne correspondent pas'
    if (!terms) errs.terms = 'Vous devez accepter les CGV pour continuer'

    return errs
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')

    const fd = new FormData(e.currentTarget)
    const errs = validate(fd)

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      scrollToFirstError()
      return
    }
    setFieldErrors({})
    setLoading(true)

    try {
      const res = await fetch(`${API}/api/auth/register/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fd.get('name'),
          email: fd.get('email'),
          siret: fd.get('siret') || undefined,
          phone: fd.get('phone') || undefined,
          password: fd.get('password'),
          isCnpas,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message ?? 'Erreur lors de l\'inscription')
      }

      setSuccess(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-5xl mb-5">🎉</div>
        <h1 className="text-2xl font-bold text-text-primary mb-3">Compte créé !</h1>
        <p className="text-text-secondary text-sm mb-6">
          {next
            ? 'Votre compte entreprise a bien été créé. Connectez-vous pour reprendre votre proposition de mission là où vous l\'aviez laissée.'
            : 'Votre compte entreprise a bien été créé. Vous pouvez maintenant vous connecter et publier vos premières missions.'}
        </p>
        <Link to={next ? `/connexion?next=${encodeURIComponent(next)}` : '/connexion'} className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
          {next ? 'Se connecter et continuer →' : 'Se connecter →'}
        </Link>
      </div>
    )
  }

  return (
    <>
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center">
            <span className="text-accent font-bold text-lg">V</span>
          </div>
          <span className="font-semibold text-text-primary text-xl tracking-tight">Velink</span>
        </div>
      </div>

      <div className="glass-card p-8">
        <div className="mb-7">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-medium mb-3">
            🏢 Compte Entreprise
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Créer un compte</h1>
          <p className="text-text-secondary text-sm mt-1.5">
            Déjà inscrit ?{' '}
            <Link to="/connexion" className="text-accent hover:text-accent-hover transition-colors">
              Se connecter
            </Link>
            {' · '}
            <Link to="/inscription/prestataire" className="text-text-muted hover:text-text-secondary transition-colors">
              Je suis prestataire
            </Link>
          </p>
        </div>

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Nom de l'entreprise */}
          <div>
            <label htmlFor="name" className="glass-label">
              Nom de l'entreprise <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              autoComplete="organization"
              placeholder="Acme SAS"
              className={`glass-input${fieldErrors.name ? ' error' : ''}`}
            />
            {fieldErrors.name && <p className="glass-field-error">{fieldErrors.name}</p>}
          </div>

          {/* SIRET */}
          <div>
            <label htmlFor="siret" className="glass-label">
              SIRET{' '}
              <span className="text-text-muted font-normal">(optionnel, 14 chiffres)</span>
            </label>
            <input
              type="text"
              id="siret"
              name="siret"
              placeholder="12345678901234"
              maxLength={14}
              className="glass-input"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="glass-label">
              Email professionnel <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              placeholder="contact@entreprise.fr"
              className={`glass-input${fieldErrors.email ? ' error' : ''}`}
            />
            {fieldErrors.email && <p className="glass-field-error">{fieldErrors.email}</p>}
          </div>

          {/* Téléphone */}
          <div>
            <label htmlFor="phone" className="glass-label">
              Téléphone{' '}
              <span className="text-text-muted font-normal">(optionnel)</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              autoComplete="tel"
              placeholder="+33 6 00 00 00 00"
              className="glass-input"
            />
          </div>

          {/* Mot de passe */}
          <div>
            <label htmlFor="password" className="glass-label">
              Mot de passe <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="new-password"
                placeholder="Min. 8 caractères"
                className={`glass-input pr-11${fieldErrors.password ? ' error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors p-1"
                aria-label={showPwd ? 'Masquer' : 'Afficher'}
              >
                {showPwd ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {fieldErrors.password && <p className="glass-field-error">{fieldErrors.password}</p>}
          </div>

          {/* Confirmation mot de passe */}
          <div>
            <label htmlFor="confirmPassword" className="glass-label">
              Confirmer le mot de passe <span className="text-danger">*</span>
            </label>
            <input
              type={showPwd ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              placeholder="••••••••"
              className={`glass-input${fieldErrors.confirmPassword ? ' error' : ''}`}
            />
            {fieldErrors.confirmPassword && (
              <p className="glass-field-error">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          {/* CNPAS */}
          <div className="rounded-xl border border-white/[0.10] bg-white/[0.04] p-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={isCnpas}
                onChange={(e) => setIsCnpas(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 accent-accent flex-shrink-0"
              />
              <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                <span className="font-semibold text-text-primary">Entreprise référencée CNPAS</span>
                <span className="block text-xs text-text-muted mt-0.5">
                  Permet de rechercher et recevoir des prestataires <strong className="text-text-secondary">ADS</strong> (Agent de Défense et Sécurité) et <strong className="text-text-secondary">SSIAP</strong> (Sécurité Incendie et Assistance à Personnes)
                </span>
              </span>
            </label>
          </div>

          {/* CGV */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="terms"
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 accent-accent flex-shrink-0"
              />
              <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                J'accepte les{' '}
                <Link to="/cgv" className="text-accent hover:text-accent-hover transition-colors">
                  conditions générales de vente
                </Link>{' '}
                et la{' '}
                <Link to="/confidentialite" className="text-accent hover:text-accent-hover transition-colors">
                  politique de confidentialité
                </Link>
              </span>
            </label>
            {fieldErrors.terms && <p className="glass-field-error mt-1">{fieldErrors.terms}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="glass-btn-accent w-full py-3 text-sm rounded-xl mt-2"
          >
            {loading ? 'Création en cours…' : 'Créer mon compte entreprise →'}
          </button>
        </form>
      </div>
    </>
  )
}
